// Responsibilities:
// - Compose entrance detection (useAvatarEntrance)
// - Provide deterministic entrance positions and stagger
// - Return wrapper motion props and the per-player label fade-in delay

import { useCallback, useRef } from 'react';
import type { HTMLMotionProps } from 'framer-motion';
import { useAvatarEntrance } from './useAvatarEntrance';
import type { Position2D } from '../types';
import type { RoomPlayer } from '@airhead-games/shared/types';
import { useAnyDialogOpen } from '@/utilities/dialogPresenceStore';

interface GridMotionOptions {
  containerWidth: number;
  containerHeight: number;
  itemSize: number;
  enableFloatEntrance?: boolean;
  entranceDuration?: number; // ms
  entranceStagger?: number; // ms per index
}

type ItemMotionProps = Pick<
  HTMLMotionProps<'div'>,
  'initial' | 'animate' | 'exit' | 'transition' | 'layout'
>;

// Smooth transition used when an existing avatar's slot moves due to grid
// re-layout (someone joined or left). Distinct from the bottom-up entry.
// Tuned to feel calm next to the multi-second entrance animation.
const REPOSITION_DURATION_S = 1.2;
const ENTRANCE_LAYOUT_SETTLE_MS = 500;

export function useAvatarGridMotion(
  players: RoomPlayer[],
  {
    containerWidth,
    containerHeight,
    itemSize,
    enableFloatEntrance = false,
    entranceDuration = 5000,
    entranceStagger = 250,
  }: GridMotionOptions
) {
  const isAnimationPaused = useAnyDialogOpen();
  const lastTargetByPlayerRef = useRef<
    Map<string, { x: number; y: number; size: number; firstTargetAt: number }>
  >(new Map());

  const { getPlayerConfig, duration } = useAvatarEntrance(players, {
    enabled: enableFloatEntrance,
    duration: entranceDuration,
  });

  // Deterministic hash for consistent randomized X start
  const hashToX = useCallback(
    (playerId: string) => {
      const h = playerId
        .split('')
        .reduce((acc, ch) => ((acc << 5) - acc + ch.charCodeAt(0)) | 0, 0);
      const percent = Math.abs(h) % 100; // 0-99
      return (containerWidth * percent) / 100;
    },
    [containerWidth]
  );

  // Total seconds the label should wait before fading in for a given player.
  // Captured by Avatar at mount time, so re-renders that flip shouldAnimate
  // do not retroactively shorten or extend the delay.
  const getLabelFadeInDelay = useCallback(
    (playerId: string, index: number): number => {
      const entrance = getPlayerConfig(playerId);
      if (isAnimationPaused || !entrance.shouldAnimate) {
        return 0;
      }
      return ((index + 1) * entranceStagger + duration) / 1000;
    },
    [getPlayerConfig, isAnimationPaused, entranceStagger, duration]
  );

  const getItemMotion = useCallback(
    (
      playerId: string,
      position: Position2D,
      index: number
    ): ItemMotionProps => {
      const entrance = getPlayerConfig(playerId);
      const now =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      const previousTarget = lastTargetByPlayerRef.current.get(playerId);
      const firstTargetAt = previousTarget?.firstTargetAt ?? now;
      const target = {
        x: position.x,
        y: position.y,
        size: itemSize,
        firstTargetAt,
      };
      lastTargetByPlayerRef.current.set(playerId, target);

      const targetChanged =
        previousTarget !== undefined &&
        (previousTarget.x !== target.x ||
          previousTarget.y !== target.y ||
          previousTarget.size !== target.size);
      const isInitialLayoutSettling =
        now - firstTargetAt < ENTRANCE_LAYOUT_SETTLE_MS;
      const shouldUseEntranceMotion =
        !isAnimationPaused &&
        entrance.shouldAnimate &&
        (!targetChanged || isInitialLayoutSettling);

      const entryInitial = {
        scale: 0,
        x: hashToX(playerId),
        y: containerHeight + 200,
      };

      return {
        // Entrants float up from below; existing players keep their current
        // animated state. Ignore mount-time measurement churn, but switch
        // later target changes to reposition instead of replaying entry.
        initial: shouldUseEntranceMotion ? entryInitial : false,
        animate: { scale: 1, x: position.x, y: position.y },
        exit: {
          scale: 0,
          opacity: 0,
          transition: { duration: 1, ease: 'easeInOut' },
        },
        transition: shouldUseEntranceMotion
          ? {
              duration: duration / 1000,
              ease: 'linear',
              delay: ((index + 1) * entranceStagger) / 1000,
            }
          : {
              // Smooth reposition when the grid re-layouts after a join/leave.
              duration: REPOSITION_DURATION_S,
              ease: 'easeInOut',
            },
        layout: 'size',
      };
    },
    [
      containerHeight,
      itemSize,
      entranceStagger,
      duration,
      getPlayerConfig,
      isAnimationPaused,
      hashToX,
    ]
  );

  return { getItemMotion, getLabelFadeInDelay };
}
